import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Dialog } from "@/components/ui/dialog";
import { useCreateAuth } from "@/hooks/use-auths";
import { toast } from "sonner";
import { HelpIcon } from "@/components/ui/help-icon";

interface CreateAuthModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: (id: string) => void;
}

export function CreateAuthModal({ open, onClose, onCreated }: CreateAuthModalProps) {
  const createMut = useCreateAuth();
  const [name, setName] = useState("");
  const [authType, setAuthType] = useState("userpass");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [passwordDigest, setPasswordDigest] = useState("");

  const handleSave = () => {
    if (!name.trim() || !username.trim()) return;
    if (authType === "userpass" && !password.trim()) return;
    if (authType === "md5" && !passwordDigest.trim()) return;
    createMut.mutate(
      {
        name: name.trim(),
        auth_type: authType,
        username: username.trim(),
        password: authType === "userpass" ? password.trim() : "",
        password_digest: authType === "md5" ? passwordDigest.trim() : null,
      },
      {
        onSuccess: (data) => {
          toast.success("Auth created");
          onCreated(data.id);
          setName("");
          setAuthType("userpass");
          setUsername("");
          setPassword("");
          setPasswordDigest("");
          onClose();
        },
        onError: (e) => toast.error(`Failed: ${(e as Error).message}`),
      }
    );
  };

  return (
    <Dialog open={open} onClose={onClose} title="Create Auth">
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Name * <HelpIcon text="Unique auth identifier. Referenced by endpoints as auth or outbound_auth." /></Label>
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="my-auth" />
        </div>
        <div className="space-y-1.5">
          <Label>Type <HelpIcon text="userpass - username/password. md5 - pre-computed MD5 digest." /></Label>
          <Select value={authType} onChange={(e) => setAuthType(e.target.value)}>
            <option value="userpass">User/Pass</option>
            <option value="md5">MD5</option>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Username * <HelpIcon text="SIP authentication username. Example: mytrunkuser." /></Label>
          <Input value={username} onChange={(e) => setUsername(e.target.value)} placeholder="my-user" />
        </div>
        {authType === "userpass" ? (
          <div className="space-y-1.5">
            <Label>Password * <HelpIcon text="SIP authentication password/secret." /></Label>
            <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••" />
          </div>
        ) : (
          <div className="space-y-1.5">
            <Label>Password Digest * <HelpIcon text='Pre-computed MD5 digest. Generate with: echo -n "myuser:somedomain:somepassword" | openssl dgst -md5' /></Label>
            <Input value={passwordDigest} onChange={(e) => setPasswordDigest(e.target.value)} placeholder="MD5 hash" />
          </div>
        )}

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={createMut.isPending || !name.trim() || !username.trim() || (authType === "userpass" && !password.trim()) || (authType === "md5" && !passwordDigest.trim())}>
            Create
          </Button>
        </div>
      </div>
    </Dialog>
  );
}